import React, { useEffect, useRef, useState } from "react";
import {
  Modal,
  Box,
  IconButton,
  Tabs,
  Tab,
  CircularProgress,
  useTheme,
  Grid2,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { useDebounce } from "../../../../../views/components/common/hooks/Debounce";
import NumericTextField from "../../../../../views/components/common/pop-up/NumericTextField";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import CustomTextField from "../../../../../views/components/common/textFields/TextField";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import { observer } from "mobx-react";
import { GetPacksParams } from "../../../stores/ShipmentStore";
import InventoryAPI from "../../../api/InventoryAPI";
import {
  InventoryPackSummary,
  InventoryVariant,
} from "../../../../../models/inventory/InventoryAdmin";
import {
  AddShipmentItemLineRequest,
  AddShipmentPackLineRequest,
} from "../../../../../models/Shipments/ShipmentInventoryLines";

interface AddPacksProps {
  open: boolean;
  getPacksParams: GetPacksParams;
  onClose: () => void;
  onSavePacks: (payload: {
    packs: AddShipmentPackLineRequest[];
    items: AddShipmentItemLineRequest[];
  }) => void;
}

type SkuTab = "items" | "packs";

type SelectedItemLine = {
  type: "ITEM";
  uniqueId: string;
  clientSku: string;
  productName?: string;
  free: number;
  quantity: number | string;
};

type SelectedPackLine = {
  type: "PACK";
  uniqueId: string;
  packId: number;
  label: string;
  buildableNow: number;
  quantity: number | string;
};

type SelectedLine = SelectedItemLine | SelectedPackLine;

const inventoryAPI = new InventoryAPI();

const AddPacksPopup: React.FC<AddPacksProps> = ({
  open,
  getPacksParams,
  onClose,
  onSavePacks,
}) => {
  const theme = useTheme();
  const [selectedLines, setSelectedLines] = useState<SelectedLine[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [infoMessage, setInfoMessage] = useState<string>("");
  const [searchText, setSearchText] = useState<string>("");
  const [skuTab, setSkuTab] = useState<SkuTab>("items");
  const [variants, setVariants] = useState<InventoryVariant[]>([]);
  const [packs, setPacks] = useState<InventoryPackSummary[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState<string>("");
  const debouncedSearchText = useDebounce(searchText, 400);
  const lastFetchedKey = useRef<string | null>(null);

  const inventoryId = getPacksParams?.inventory_id;

  useEffect(() => {
    if (!open || !inventoryId) return;

    const cacheKey = `${inventoryId}-${skuTab}-${debouncedSearchText.trim()}`;
    if (lastFetchedKey.current === cacheKey) return;
    lastFetchedKey.current = cacheKey;

    const fetchList = async () => {
      setLoadingList(true);
      setListError("");
      try {
        const search = debouncedSearchText.trim() || undefined;
        if (skuTab === "items") {
          const response = await inventoryAPI.getVariants({
            inventoryId,
            search,
          });
          setVariants(response?.variants ?? []);
        } else {
          const response = await inventoryAPI.listPacks({
            inventoryId,
            search,
          });
          setPacks(response?.packs ?? []);
        }
      } catch (error: any) {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to load SKUs";
        setListError(message);
        if (skuTab === "items") setVariants([]);
        else setPacks([]);
      } finally {
        setLoadingList(false);
      }
    };

    fetchList();
  }, [open, inventoryId, debouncedSearchText, skuTab]);

  useEffect(() => {
    if (!open) {
      setSearchText("");
      setSkuTab("items");
      setSelectedLines([]);
      setErrorMessage("");
      setInfoMessage("");
      setListError("");
      setVariants([]);
      setPacks([]);
      lastFetchedKey.current = null;
    }
  }, [open]);

  const generateUniqueId = () =>
    `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  const handleAddItem = (variant: InventoryVariant) => {
    const alreadyAdded = selectedLines.some(
      (line) => line.type === "ITEM" && line.clientSku === variant.clientSku,
    );
    if (alreadyAdded) {
      setErrorMessage("");
      setInfoMessage(
        `"${variant.clientSku}" is already added. Update the quantity on the right instead.`,
      );
      return;
    }

    setSelectedLines((prev) => [
      ...prev,
      {
        type: "ITEM",
        uniqueId: generateUniqueId(),
        clientSku: variant.clientSku,
        productName: variant.productName,
        free: variant.free,
        quantity: 1,
      },
    ]);
    setErrorMessage("");
    setInfoMessage("");
  };

  const handleAddPack = (pack: InventoryPackSummary) => {
    const alreadyAdded = selectedLines.some(
      (line) => line.type === "PACK" && line.packId === pack.packId,
    );
    if (alreadyAdded) {
      setErrorMessage("");
      setInfoMessage(
        `"${pack.label}" is already added. Update the quantity on the right instead.`,
      );
      return;
    }

    setSelectedLines((prev) => [
      ...prev,
      {
        type: "PACK",
        uniqueId: generateUniqueId(),
        packId: pack.packId,
        label: pack.label,
        buildableNow: pack.buildableNow,
        quantity: 1,
      },
    ]);
    setErrorMessage("");
    setInfoMessage("");
  };

  const handleRemoveLine = (uniqueId: string) => {
    setSelectedLines((prev) =>
      prev.filter((line) => line.uniqueId !== uniqueId),
    );
  };

  const handleQuantityChange = (uniqueId: string, value: number | string) => {
    setSelectedLines((prev) =>
      prev.map((line) =>
        line.uniqueId === uniqueId ? { ...line, quantity: value } : line,
      ),
    );
  };

  const validateLines = () => {
    setInfoMessage("");
    if (selectedLines.length === 0) {
      setErrorMessage("Please add at least one item or pack.");
      return false;
    }

    for (const line of selectedLines) {
      const qty = Number(line.quantity);
      if (!line.quantity || !Number.isFinite(qty) || qty <= 0) {
        setErrorMessage("Please ensure all lines have a valid quantity.");
        return false;
      }
    }

    setErrorMessage("");
    return true;
  };

  const handleSave = () => {
    if (!validateLines()) return;

    try {
      const packsPayload: AddShipmentPackLineRequest[] = selectedLines
        .filter((line): line is SelectedPackLine => line.type === "PACK")
        .map((line) => ({
          packId: line.packId,
          qty: Number(line.quantity),
        }));

      const itemsPayload: AddShipmentItemLineRequest[] = selectedLines
        .filter((line): line is SelectedItemLine => line.type === "ITEM")
        .map((line) => ({
          clientSku: line.clientSku,
          qty: Number(line.quantity),
        }));

      onSavePacks({ packs: packsPayload, items: itemsPayload });
    } catch (error: any) {
      setErrorMessage(
        error?.message || "Failed to prepare shipment lines for save.",
      );
    }
  };

  const listEmpty =
    !loadingList &&
    !listError &&
    (skuTab === "items" ? variants.length === 0 : packs.length === 0);

  return (
    <Modal
      open={open}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: { xs: 2, md: 0 },
      }}
    >
      <Box
        sx={{
          padding: { xs: 2, md: 3 },
          backgroundColor: "background.paper",
          width: { xs: "100%", sm: "95%", md: "85%", lg: "70%" },
          maxWidth: 900,
          minHeight: 520,
          borderRadius: 2,
          boxShadow:
            "0 24px 48px -12px rgba(0, 0, 0, 0.18), 0 0 0 1px rgba(0, 0, 0, 0.05)",
          maxHeight: { xs: "90vh", md: "85vh" },
          overflowY: "auto",
          outline: "none",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <CustomTypography
          variant="heading6"
          fontWeight={600}
          sx={{ mb: 2, color: "text.primary" }}
        >
          Select SKU for the address
        </CustomTypography>

        <Grid2
          container
          spacing={2}
          sx={{ flex: 1, overflow: "hidden", minHeight: 0 }}
        >
          <Grid2
            size={{ xs: 12, md: 4 }}
            sx={{
              maxHeight: { xs: "45vh", md: "60vh" },
              overflowY: "auto",
              borderRight: { xs: 0, md: 1 },
              borderBottom: { xs: 1, md: 0 },
              borderColor: "divider",
              pr: { xs: 0, md: 2 },
              pb: { xs: 2, md: 0 },
            }}
          >
            <CustomTextField
              placeholder="Search by SKU"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value || "")}
              width="100%"
              customStyles={{ marginBottom: 8 }}
            />
            <Tabs
              value={skuTab}
              onChange={(_, v: SkuTab) => setSkuTab(v)}
              sx={{
                minHeight: 36,
                mb: 1,
                "& .MuiTab-root": {
                  minHeight: 36,
                  py: 0.5,
                  fontSize: "0.8rem",
                },
                "& .MuiTabs-indicator": {
                  height: 2,
                  borderRadius: "2px 2px 0 0",
                },
              }}
            >
              <Tab label="Items" value="items" />
              <Tab label="Packs" value="packs" />
            </Tabs>

            {loadingList && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {listError && (
              <CustomTypography
                variant="body2"
                sx={{ color: theme.palette.error.main, py: 1 }}
              >
                {listError}
              </CustomTypography>
            )}

            {listEmpty && (
              <CustomTypography
                variant="body2"
                sx={{ color: "text.secondary", py: 2 }}
              >
                No {skuTab === "items" ? "items" : "packs"} found
              </CustomTypography>
            )}

            {skuTab === "items" &&
              !loadingList &&
              variants.map((variant) => (
                <Box
                  key={variant.id}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 1.5,
                    px: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    "&:hover": { backgroundColor: "action.hover" },
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
                    <CustomTypography
                      variant="body2"
                      fontWeight={600}
                      sx={{ fontSize: "0.85rem" }}
                    >
                      {variant.clientSku}
                    </CustomTypography>
                    {variant.productName && (
                      <CustomTypography
                        variant="body2"
                        sx={{
                          fontSize: "0.75rem",
                          color: "text.secondary",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {variant.productName}
                      </CustomTypography>
                    )}
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <CustomTypography
                      variant="body2"
                      fontWeight={600}
                      sx={{ fontSize: "0.85rem", color: "text.secondary" }}
                    >
                      {variant.free}
                    </CustomTypography>
                    <IconButton
                      onClick={() => handleAddItem(variant)}
                      color="primary"
                      size="small"
                      sx={{
                        "&:hover": {
                          backgroundColor: "primary.main",
                          color: "primary.contrastText",
                        },
                      }}
                    >
                      <AddIcon sx={{ fontSize: "1rem" }} />
                    </IconButton>
                  </Box>
                </Box>
              ))}

            {skuTab === "packs" &&
              !loadingList &&
              packs.map((pack) => (
                <Box
                  key={pack.packId}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 1.5,
                    px: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    "&:hover": { backgroundColor: "action.hover" },
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
                    <CustomTypography
                      variant="body2"
                      fontWeight={600}
                      sx={{ fontSize: "0.85rem" }}
                    >
                      {pack.label}
                    </CustomTypography>
                    <CustomTypography
                      variant="body2"
                      sx={{
                        fontSize: "0.75rem",
                        color: "text.secondary",
                      }}
                    >
                      Pack #{pack.packId}
                    </CustomTypography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <CustomTypography
                      variant="body2"
                      fontWeight={600}
                      sx={{ fontSize: "0.85rem", color: "text.secondary" }}
                    >
                      {pack.buildableNow}
                    </CustomTypography>
                    <IconButton
                      onClick={() => handleAddPack(pack)}
                      color="primary"
                      size="small"
                      sx={{
                        "&:hover": {
                          backgroundColor: "primary.main",
                          color: "primary.contrastText",
                        },
                      }}
                    >
                      <AddIcon sx={{ fontSize: "1rem" }} />
                    </IconButton>
                  </Box>
                </Box>
              ))}
          </Grid2>

          <Grid2
            size={{ xs: 12, md: 8 }}
            sx={{
              overflow: "auto",
              maxHeight: { xs: "45vh", md: "60vh" },
              borderRadius: 1,
              backgroundColor: { xs: "action.hover", md: "transparent" },
            }}
          >
            {selectedLines.length === 0 && (
              <CustomTypography
                variant="body2"
                sx={{ color: "text.secondary", p: 2 }}
              >
                Added items and packs will appear here
              </CustomTypography>
            )}

            {selectedLines.map((line) => (
              <Box
                key={line.uniqueId}
                sx={{
                  position: "relative",
                  mb: 2,
                  p: 2,
                  borderRadius: 1.5,
                  boxShadow:
                    "0 2px 8px rgba(0,0,0,0.08), 0 0 1px rgba(0,0,0,0.06)",
                  backgroundColor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <IconButton
                  sx={{
                    color: "grey.600",
                    position: "absolute",
                    top: 4,
                    right: 4,
                    backgroundColor: "action.hover",
                    borderRadius: "50%",
                    "&:hover": {
                      backgroundColor: "action.selected",
                    },
                  }}
                  onClick={() => handleRemoveLine(line.uniqueId)}
                  size="small"
                >
                  <CloseIcon sx={{ fontSize: "14px" }} />
                </IconButton>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 0.5,
                    paddingTop: 1,
                    paddingRight: 3,
                    gap: 2,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <CustomTypography
                      variant="body2"
                      fontWeight={600}
                      sx={{ fontSize: "0.875rem" }}
                    >
                      {line.type === "ITEM" ? line.clientSku : line.label}
                    </CustomTypography>
                    <CustomTypography
                      variant="body2"
                      sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                    >
                      {line.type === "ITEM"
                        ? line.productName || `Free: ${line.free}`
                        : `Pack #${line.packId} · Buildable: ${line.buildableNow}`}
                    </CustomTypography>
                  </Box>
                  <NumericTextField
                    sx={{
                      "& .MuiInputBase-input": {
                        fontSize: "12px",
                        width: "60px",
                        textAlign: "center",
                      },
                    }}
                    type="number"
                    placeholder="Qty"
                    size="small"
                    value={line.quantity}
                    onChange={(e) =>
                      handleQuantityChange(
                        line.uniqueId,
                        e.target.value === "" ? "" : parseInt(e.target.value),
                      )
                    }
                  />
                </Box>
              </Box>
            ))}
          </Grid2>
        </Grid2>

        {(errorMessage || infoMessage) && (
          <CustomTypography
            variant="body2"
            sx={{
              mt: 2,
              textAlign: "center",
              color: errorMessage
                ? theme.palette.error.main
                : theme.palette.warning.main,
            }}
          >
            {errorMessage || infoMessage}
          </CustomTypography>
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            mt: 2,
            pt: 2,
            borderTop: 1,
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          <AppButton
            label="Cancel"
            variant="secondary"
            onClick={() => onClose()}
            customStyles={{ px: { xs: 2, md: 3 } }}
          />
          <AppButton
            label="Save Details"
            variant="primary"
            onClick={handleSave}
            disabled={selectedLines.length === 0}
            customStyles={{ px: { xs: 2, md: 3 } }}
          />
        </Box>
      </Box>
    </Modal>
  );
};

export default observer(AddPacksPopup);
